import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Wind } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, DIFFICULTIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TutorialCard } from "@/components/TutorialCard";
import { AssignedSection } from "@/components/AssignedSection";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const Pill = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"}`}>
    {children}
  </button>
);

export default function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState(null);
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState({});
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/tutorials", { params: { search: search || undefined, category: category || undefined, difficulty: difficulty || undefined } })
        .then((r) => setTutorials(r.data))
        .catch((e) => toast.error(errorMessage(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [search, category, difficulty]);

  useEffect(() => {
    if (!user) { setProgress({}); return; }
    api.get("/progress").then((r) => setProgress(Object.fromEntries(r.data.map((p) => [p.tutorial_id, p])))).catch(() => {});
  }, [user]);

  const onBookmark = async (t) => {
    if (!user) { toast("Sign in to save bookmarks"); navigate("/login"); return; }
    try {
      const r = await api.post(`/progress/${t.id}/bookmark`);
      setProgress((p) => ({ ...p, [t.id]: r.data }));
      toast.success(r.data.is_bookmarked ? "Added to My Learning" : "Bookmark removed");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const counts = useMemo(() => ({ total: tutorials?.length || 0 }), [tutorials]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="library-page">
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
        <div className="lg:col-span-7 space-y-4 rise">
          <p className="eyebrow">Tutorials</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-slate-900">
            Learn the app. <span className="text-indigo-600">Practise calm.</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
            Short guides on getting around, plus interactive mindfulness and breathing exercises you can return to between sessions.
          </p>
        </div>
        <div className="lg:col-span-5 rise" style={{ animationDelay: "120ms" }}>
          <div className="rounded-3xl bg-indigo-600 text-white p-6 sm:p-8 relative overflow-hidden grain">
            <Wind className="absolute -right-6 -bottom-6 w-40 h-40 text-white/10" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Tip for today</p>
            <p className="font-display text-xl sm:text-2xl font-bold mt-2 leading-snug">One slow breath out is longer than one in. That's the whole secret.</p>
            <p className="text-sm text-indigo-100 mt-3">Try the Calm Breath exercise — three minutes, no breath-hold.</p>
          </div>
        </div>
      </section>

      <AssignedSection compact />

      <section className="space-y-4 mb-10">
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tutorials by title…"
            className="h-12 pl-11 rounded-full bg-white border-slate-200 text-base" data-testid="search-tutorials-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill active={!category} onClick={() => setCategory("")} testId="category-filter-all">All</Pill>
          {categories.map((c) => (
            <Pill key={c.id} active={category === c.id} onClick={() => setCategory(c.id)} testId={`category-filter-${c.slug}`}>{c.name}</Pill>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill active={!difficulty} onClick={() => setDifficulty("")} testId="difficulty-filter-all">Any level</Pill>
          {DIFFICULTIES.map((d) => (
            <Pill key={d} active={difficulty === d} onClick={() => setDifficulty(d)} testId={`difficulty-filter-${d.toLowerCase()}`}>{d}</Pill>
          ))}
        </div>
      </section>

      {tutorials === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-3xl" />)}
        </div>
      ) : tutorials.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 p-12 text-center" data-testid="library-empty">
          <p className="font-display text-xl font-bold text-slate-800">No tutorials match</p>
          <p className="text-slate-500 mt-1">Try a different search or clear the filters.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-4" data-testid="results-count">{counts.total} tutorial{counts.total === 1 ? "" : "s"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="tutorials-grid">
            {tutorials.map((t, i) => <TutorialCard key={t.id} tutorial={t} progress={progress[t.id]} onBookmark={onBookmark} index={i} />)}
          </div>
        </>
      )}
    </div>
  );
}
